import { Icon } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Icon";

import { Text } from "atoms/Text";
import { useClassNames } from "@/utils/classNames";
import { Image } from "@/components/atoms/Image";

import { RailTitleProps } from "./types";
import styles from "./RailTitle.module.scss";

/**
 * RailTitle component renders a title and optional icon for a rail.
 *
 * @param variant - The type of the rail, defaults to Hero34
 * @param title - Optional title for the rail
 * @param icon - Optional icon to display alongside the title
 * @param imgSrc - Optional image source to display alongside the title (used if StreamingIcon has color)
 * @param className - Additional CSS class for styling
 * @param attrs - Additional HTML attributes for the div element
 * @returns A rail title component
 *
 * @example
 * ```tsx
 * <RailTitle
 *   variant={RailVariant.Hero34}
 *   title="Featured Content"
 *   icon={StreamingIcons.Lock}
 * />
 * ```
 */
const RailTitle = ({
  variant,
  title,
  icon,
  imgSrc,
  className,
  ...attrs
}: RailTitleProps) => {
  const cn = useClassNames(
    styles,
    styles["rail-title"],
    styles[`variant-${variant}`],
    className,
  );

  return (
    <div className={cn} {...attrs}>
      {icon && <Icon icon={icon} className={styles["icon"]} />}
      {imgSrc && (
        <Image
          src={imgSrc}
          className={styles["icon-img"]}
          allowColorOverride={false}
        />
      )}
      <Text variant="h2" className={styles["title"]}>
        {title}
      </Text>
    </div>
  );
};

export { RailTitle };
